/**
 * Live audio analyser — spectral-flux beat detection + adaptive band energy.
 *
 * Ported (loosely) from `app/server.py`'s `BeatAnalyzer`. Differences from
 * the Python version:
 *   - We use the WebAudio AnalyserNode for the FFT (it gives us a smoothed
 *     magnitude spectrum cheaply), then run our own spectral-flux + adaptive
 *     thresholding on top.
 *   - No SL hooks. Just produces `AudioFeatures` to be read by the renderer.
 *   - Beat output is an envelope (decays over time) rather than a one-frame
 *     boolean, so the renderer can sample at any frame rate without missing
 *     beats.
 */

import {
  AudioFeatures,
  FeatureProvider,
  ZERO_FEATURES,
} from "./types";

const FFT_SIZE = 1024;
const SMOOTHING = 0.55;

/**
 * Universal audio-processing parameters. Live across all scenes — the
 * Source tab "Audio Processing" sliders write here, every analyser
 * instance reads from this object every frame.
 *
 * "Wired" fields actually do something today. "Inert" fields are saved
 * (so the UI is stateful) but their effect lands in a follow-up pass.
 */
export interface AudioProcessingParams {
  /** Wired — clamped to one of {1024,2048,4096,8192,16384} on apply. */
  fftSize: number;
  /** Wired — AnalyserNode.minDecibels. */
  dbFloor: number;
  /** Wired — AnalyserNode.maxDecibels. */
  dbCeiling: number;
  /** Wired — AnalyserNode.smoothingTimeConstant (0..1). */
  peakDecay: number;
  /** Wired — multiplier on published mid (0..2). */
  midReactivity: number;
  /** Wired — multiplier on published treble (0..2). */
  highReactivity: number;
  /** Wired — multiplier on published bass/mid/treble (0..4). */
  gain: number;
  /** Inert (surface-only) — saved, not yet applied. */
  noiseGate: number;
  /** Inert. */
  bassAttenuation: number;
  /** Inert. */
  spectrumCap: number;
  /** Inert. */
  spectrumCompress: number;
  /** Inert — "input" vs "processed" spectrum view. */
  spectrumFunction: "input" | "processed";
}

export const DEFAULT_AUDIO_PROCESSING: AudioProcessingParams = {
  fftSize: 1024,
  dbFloor: -90,
  dbCeiling: -10,
  peakDecay: 0.55,
  midReactivity: 1,
  highReactivity: 1,
  gain: 1,
  noiseGate: 0,
  bassAttenuation: 0,
  spectrumCap: 1,
  spectrumCompress: 0,
  spectrumFunction: "processed",
};

/** Mutable global. UI writes here, analysers read here. */
export const globalAudioParams: AudioProcessingParams = { ...DEFAULT_AUDIO_PROCESSING };

function clampFftSize(v: number): 1024 | 2048 | 4096 | 8192 | 16384 {
  if (v <= 1024) return 1024;
  if (v <= 2048) return 2048;
  if (v <= 4096) return 4096;
  if (v <= 8192) return 8192;
  return 16384;
}

/** Ring-buffer mean for adaptive thresholds. */
class Rolling {
  private buf: Float32Array;
  private idx = 0;
  private filled = 0;

  constructor(size: number) {
    this.buf = new Float32Array(size);
  }
  push(v: number): void {
    this.buf[this.idx] = v;
    this.idx = (this.idx + 1) % this.buf.length;
    if (this.filled < this.buf.length) this.filled++;
  }
  mean(): number {
    if (this.filled === 0) return 0;
    let s = 0;
    for (let i = 0; i < this.filled; i++) s += this.buf[i];
    return s / this.filled;
  }
  get count(): number {
    return this.filled;
  }
}

export class LiveAnalyser implements FeatureProvider {
  private readonly node: AnalyserNode;
  private mag: Uint8Array<ArrayBuffer>;
  private prevMag: Float32Array;
  private readonly fluxHistory = new Rolling(80);
  private readonly bassFluxHistory = new Rolling(80);
  private readonly beatTimes: number[] = [];
  private readonly intervals: number[] = [];

  // Adaptive band peaks (mirror server.py's per-band normalization)
  private bassPeak = 1e-3;
  private midPeak = 1e-3;
  private treblePeak = 1e-3;

  // Thresholds & gating
  private readonly thresholdMult = 1.45;
  private readonly bassPeakDecay = 0.998;
  private readonly peakDecay = 0.9995;
  private readonly minBeatIntervalFloor = 0.12; // s
  private readonly minBeatIntervalDefault = 0.25;

  // Beat envelope
  private beatEnv = 0;
  private readonly beatDecay = 0.0025; // per ms; full decay ~400ms
  private lastTickMs = performance.now();
  private lastBeatAt = 0; // monotonic seconds

  // Bin indices for bass/mid/treble (computed from sample rate)
  private bassLo = 0;
  private bassHi = 0;
  private midLo = 0;
  private midHi = 0;
  private trebLo = 0;
  private trebHi = 0;

  // Latest published snapshot
  private snapshot: AudioFeatures = { ...ZERO_FEATURES };
  private bpm = 0;
  private beatCount = 0;
  private analysisTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * @param ctx     The shared AudioContext.
   * @param source  Anything that can be `.connect()`'d into our analyser.
   */
  constructor(
    private readonly ctx: AudioContext,
    source: AudioNode,
  ) {
    this.node = ctx.createAnalyser();
    this.node.fftSize = clampFftSize(globalAudioParams.fftSize || FFT_SIZE);
    this.node.smoothingTimeConstant = globalAudioParams.peakDecay ?? SMOOTHING;
    this.node.minDecibels = globalAudioParams.dbFloor ?? -90;
    this.node.maxDecibels = globalAudioParams.dbCeiling ?? -10;

    this.mag = new Uint8Array(new ArrayBuffer(this.node.frequencyBinCount));
    this.prevMag = new Float32Array(this.node.frequencyBinCount);

    // Source -> analyser. We don't connect onward to ctx.destination here;
    // the caller is responsible for routing audio to the speakers (e.g. a
    // file source connects its element to destination separately).
    source.connect(this.node);

    this.computeBands();
    this.tick();
    this.analysisTimer = setInterval(() => this.tick(), 1000 / 60);
  }

  private computeBands(): void {
    const nyquist = this.ctx.sampleRate / 2;
    const binCount = this.node.frequencyBinCount;
    const binFor = (hz: number): number =>
      Math.max(0, Math.min(binCount - 1, Math.round((hz / nyquist) * binCount)));
    this.bassLo = binFor(20);
    this.bassHi = binFor(250);
    this.midLo = binFor(250);
    this.midHi = binFor(4000);
    this.trebLo = binFor(4000);
    this.trebHi = binFor(16000);
  }

  private minBeatInterval(): number {
    if (this.bpm <= 30) return this.minBeatIntervalDefault;
    return Math.max(this.minBeatIntervalFloor, 30 / this.bpm);
  }

  private updateBpm(nowSec: number): void {
    this.beatTimes.push(nowSec);
    if (this.beatTimes.length > 20) this.beatTimes.shift();
    if (this.beatTimes.length < 2) return;
    this.intervals.length = 0;
    for (let i = 1; i < this.beatTimes.length; i++) {
      const iv = this.beatTimes[i] - this.beatTimes[i - 1];
      if (iv > 0.25 && iv < 1.5) this.intervals.push(iv);
    }
    if (this.intervals.length === 0) return;
    this.intervals.sort((a, b) => a - b);
    let avg: number;
    if (this.intervals.length >= 5) {
      const q1 = Math.floor(this.intervals.length / 4);
      const q3 = Math.floor((3 * this.intervals.length) / 4);
      let s = 0;
      let n = 0;
      for (let i = q1; i <= q3; i++) {
        s += this.intervals[i];
        n++;
      }
      avg = s / n;
    } else {
      avg = this.intervals[Math.floor(this.intervals.length / 2)];
    }
    this.bpm = 60 / avg;
  }

  private applyGlobalAudioParams(): void {
    const p = globalAudioParams;
    const wantFft = clampFftSize(p.fftSize || FFT_SIZE);
    if (this.node.fftSize !== wantFft) {
      try {
        this.node.fftSize = wantFft;
        this.mag = new Uint8Array(new ArrayBuffer(this.node.frequencyBinCount));
        this.prevMag = new Float32Array(this.node.frequencyBinCount);
        this.computeBands();
      } catch {
        /* AnalyserNode rejects mid-stream changes on some browsers; ignore */
      }
    }
    if (this.node.smoothingTimeConstant !== p.peakDecay) {
      this.node.smoothingTimeConstant = Math.max(0, Math.min(0.99, p.peakDecay));
    }
    if (this.node.minDecibels !== p.dbFloor && p.dbFloor < p.dbCeiling) {
      this.node.minDecibels = p.dbFloor;
    }
    if (this.node.maxDecibels !== p.dbCeiling && p.dbFloor < p.dbCeiling) {
      this.node.maxDecibels = p.dbCeiling;
    }
  }

  private tick(): void {
    const now = performance.now();
    const dt = now - this.lastTickMs;
    this.lastTickMs = now;
    const nowSec = now / 1000;

    this.applyGlobalAudioParams();

    this.node.getByteFrequencyData(this.mag);

    // Per-band raw mean (0..255)
    const meanRange = (lo: number, hi: number): number => {
      let s = 0;
      const n = hi - lo + 1;
      for (let i = lo; i <= hi; i++) s += this.mag[i];
      return n > 0 ? s / n / 255 : 0;
    };

    const bassRaw = meanRange(this.bassLo, this.bassHi);
    const midRaw = meanRange(this.midLo, this.midHi);
    const trebRaw = meanRange(this.trebLo, this.trebHi);

    // Adaptive normalize
    if (bassRaw > this.bassPeak) this.bassPeak = bassRaw;
    else this.bassPeak *= this.bassPeakDecay;
    if (midRaw > this.midPeak) this.midPeak = midRaw;
    else this.midPeak *= this.peakDecay;
    if (trebRaw > this.treblePeak) this.treblePeak = trebRaw;
    else this.treblePeak *= this.peakDecay;

    const bassN = this.bassPeak > 0 ? Math.min(1, bassRaw / this.bassPeak) : 0;
    const midN = this.midPeak > 0 ? Math.min(1, midRaw / this.midPeak) : 0;
    const trebleN = this.treblePeak > 0 ? Math.min(1, trebRaw / this.treblePeak) : 0;

    // Universal post-normalize gain + per-band reactivity (Audio Processing
    // sliders in the Source tab). Applied to the published values only —
    // the beat-detection path below still uses the raw normalized values
    // so reactivity tweaks don't destabilize BPM tracking.
    const gain = Math.max(0, Math.min(4, globalAudioParams.gain));
    const midR = Math.max(0, Math.min(2, globalAudioParams.midReactivity));
    const highR = Math.max(0, Math.min(2, globalAudioParams.highReactivity));

    // Noise gate — values below the gate are zeroed out so room hiss and
    // very-low-level bins stop driving the visuals. Treated as a fraction
    // of the normalized band (0..1).
    const gate = Math.max(0, Math.min(1, globalAudioParams.noiseGate));
    const gated = (v: number): number => (v < gate ? 0 : (v - gate) / (1 - gate || 1));
    const bassG = gate > 0 ? gated(bassN) : bassN;
    const midG = gate > 0 ? gated(midN) : midN;
    const trebleG = gate > 0 ? gated(trebleN) : trebleN;

    // Bass attenuation — pulls the bass band down without losing the
    // beat-detection signal (which still uses raw bassN). 0 = no change.
    const bassAtt = Math.max(0, Math.min(1, globalAudioParams.bassAttenuation));
    const bassPostAtt = bassG * (1 - bassAtt);

    // Spectrum compression — log-shapes each band so quieter values lift
    // toward the ceiling without crushing peaks. 0 = linear, 1 = sqrt-shaped.
    const compress = Math.max(0, Math.min(1, globalAudioParams.spectrumCompress));
    const compressBand = (v: number): number =>
      compress > 0 ? v * (1 - compress) + Math.sqrt(Math.max(0, v)) * compress : v;

    // Spectrum cap — hard ceiling on any band before publish. 0 = silence,
    // 1 = full range.
    const cap = Math.max(0, Math.min(1, globalAudioParams.spectrumCap));

    // Spectrum function selector — "input" publishes the raw normalized
    // value (skipping gain/reactivity/atten/compress), "processed" (default)
    // applies the full chain.
    const useRaw = globalAudioParams.spectrumFunction === "input";
    const bass = useRaw
      ? Math.min(cap, bassN)
      : Math.min(cap, compressBand(bassPostAtt) * gain);
    const mid = useRaw
      ? Math.min(cap, midN)
      : Math.min(cap, compressBand(midG) * gain * midR);
    const treble = useRaw
      ? Math.min(cap, trebleN)
      : Math.min(cap, compressBand(trebleG) * gain * highR);

    // Spectral flux (positive deltas across all bins) + bass-only flux for
    // breakdown-aware kick rescue (mirrors server.py).
    let flux = 0;
    let bassFlux = 0;
    for (let i = 0; i < this.mag.length; i++) {
      const d = this.mag[i] - this.prevMag[i];
      if (d > 0) {
        flux += d;
        if (i >= this.bassLo && i <= this.bassHi) bassFlux += d;
      }
      this.prevMag[i] = this.mag[i];
    }
    flux /= this.mag.length;
    bassFlux /= Math.max(1, this.bassHi - this.bassLo + 1);

    this.fluxHistory.push(flux);
    this.bassFluxHistory.push(bassFlux);

    let isBeat = false;
    if (this.fluxHistory.count >= 10) {
      const meanFlux = this.fluxHistory.mean();
      const meanBassFlux = this.bassFluxHistory.mean();
      const threshold = meanFlux * this.thresholdMult;

      // Bass-kick rescue — see server.py for rationale. Uses the
      // un-multiplied bass value so the user's gain/reactivity sliders
      // don't destabilize beat tracking.
      const bassKick =
        bassFlux > meanBassFlux * 2.0 &&
        bassFlux > meanFlux * 0.5 &&
        bassN > 0.55;

      const minInterval = this.minBeatInterval();
      if (
        (flux > threshold || bassKick) &&
        nowSec - this.lastBeatAt > minInterval
      ) {
        isBeat = true;
        this.lastBeatAt = nowSec;
        this.updateBpm(nowSec);
        this.beatCount++;
        this.beatEnv = 1;
      }
    }

    // Decay beat envelope
    this.beatEnv = Math.max(0, this.beatEnv - this.beatDecay * dt);
    if (isBeat) this.beatEnv = 1;

    const level = (bass + mid + treble) / 3;
    this.snapshot = {
      bass,
      mid,
      treble,
      level,
      bpm: this.bpm,
      beat: this.beatEnv,
      beatCount: this.beatCount,
      t: now,
      // Pass the raw byte FFT through as a normalized 0..1 view. Avoid
      // allocating per frame by reusing a single backing buffer.
      spectrum: this._spectrum01(),
      // Time-domain waveform for the DrawWave overlay layer.
      waveform: this._waveform(),
    };
  }

  /** Lazily build + reuse a Float32Array view of the byte FFT, normalized 0..1. */
  private _spectrum01Buf: Float32Array | null = null;
  private _spectrum01(): Float32Array {
    const src = this.mag;
    if (!this._spectrum01Buf || this._spectrum01Buf.length !== src.length) {
      this._spectrum01Buf = new Float32Array(src.length);
    }
    const dst = this._spectrum01Buf;
    for (let i = 0; i < src.length; i++) dst[i] = src[i] / 255;
    return dst;
  }

  /**
   * Pull the analyser's time-domain PCM as a normalized -1..+1 array.
   * Reads the FULL fftSize samples (1024) from getByteTimeDomainData,
   * then converts to centered floats. Reused across frames.
   */
  private _waveformBuf: Uint8Array<ArrayBuffer> | null = null;
  private _waveformOut: Float32Array | null = null;
  private _waveform(): Float32Array {
    const n = this.node.fftSize;
    if (!this._waveformBuf || this._waveformBuf.length !== n) {
      // Use an explicit ArrayBuffer (not SharedArrayBuffer) so the
      // Uint8Array's storage type matches WebAudio's expectation.
      this._waveformBuf = new Uint8Array(new ArrayBuffer(n));
      this._waveformOut = new Float32Array(n);
    }
    this.node.getByteTimeDomainData(this._waveformBuf);
    const out = this._waveformOut!;
    for (let i = 0; i < n; i++) {
      // Bytes are 0..255 with silence at 128. Convert to -1..+1.
      out[i] = (this._waveformBuf[i] - 128) / 128;
    }
    return out;
  }

  read(): AudioFeatures {
    return this.snapshot;
  }

  stop(): void {
    if (this.analysisTimer !== null) clearInterval(this.analysisTimer);
    this.analysisTimer = null;
    try {
      this.node.disconnect();
    } catch {
      /* already disconnected */
    }
  }
}
